import type { printOnDemand, ID } from "common/utils/models";
import { Button } from "@design-lab/shared/ui";
import { PlusCircle } from "lucide-react";
import { useCompositionsActions } from "@design-lab/entities/composition";
import { CanvasProductCompositions } from "./CanvasProductCompositions";

type CanvasProductCompositionsCarouselProps = {
  compositions: printOnDemand.getProduct.Composition[];
  selectedId: ID | undefined;
  onSelect: (id: ID) => void;
  onAddMoreProduct?: (() => void) | undefined;
};

export function CanvasProductCompositionsCarousel(props: CanvasProductCompositionsCarouselProps) {
  const { compositions, selectedId, onSelect, onAddMoreProduct } = props;
  const { removeComposition } = useCompositionsActions();

  const compositionsToDisplay = compositions.filter(composition =>
    Boolean(composition.surfaces?.front?.picture || composition.surfaces?.frontLeftChest?.picture)
  );

  const showRemove = compositionsToDisplay.length > 1;

  const handleRemove = (id: ID) => {
    removeComposition(id);
  };

  if (compositionsToDisplay.length === 0) {
    return null;
  }

  return (
    <div className="absolute bottom-4 left-1/2 z-[2] -translate-x-1/2">
      <div className="bg-background relative inline-flex rounded-xl p-1 shadow-lg">
        <div
          className="flex min-w-0 max-w-[200px] items-center gap-[5px] overflow-x-auto pr-[45px] pt-1 md:max-w-[290px]"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch"
          }}
        >
          {compositionsToDisplay.map(composition => (
            <div key={composition.id} className="relative h-10 w-10 shrink-0">
              <CanvasProductCompositions
                composition={composition}
                isSelected={composition.id === selectedId}
                onSelect={onSelect}
                onRemove={showRemove ? handleRemove : undefined}
              />
            </div>
          ))}
        </div>

        {onAddMoreProduct && (
          <Button
            data-testid="add-products-button"
            onPress={onAddMoreProduct}
            variant="primary"
            size="icon_lg"
            className="absolute right-1 top-[calc(50%+2px)] -translate-y-1/2 bg-blue-700 hover:bg-blue-800"
          >
            <PlusCircle className="size-6" />
          </Button>
        )}
      </div>
    </div>
  );
}
